import { defineStore } from "pinia";
import { groupBy } from 'lodash-es';
import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter } from "~/google_analytics/events.enum";
declare const dht: any;

const QUOTE_CACHE_KEY = "cart_quote_cache";

function sanitizeLocation(location: any) {
  return {
    gps: {
      latitude: location?.gps?.latitude,
      longitude: location?.gps?.longitude,
    },
    area_code: location?.area_code,
    city: {
      code: location?.city?.code,
    },
  };
} 

function getCartHash(items: any[]): string {
  return items.map((i) => `${i.sku_id}:${i.qty}`).sort().join("|");
}
function saveQuoteToStorage(cartHash: string, transactionId: string, data: any) {
  const expiresAt = data?.[0]?.expires_at; 
  const location = useLocation();
  const geolocation = useGeoLocation();

  const safeLocation = sanitizeLocation(geolocation.selectedAddress || location);
  let safeData = data;
  try {
    safeData = JSON.parse(JSON.stringify(data));
  } catch {
    safeData = data;
  }

  localStorage.setItem(
    QUOTE_CACHE_KEY,
    JSON.stringify({ cartHash, transactionId, data: safeData, expiresAt, location: safeLocation })
  );
}
function getQuoteFromStorage(): QuoteCache | null {
  try {
    const raw = localStorage.getItem(QUOTE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function clearQuoteStorage() {
  localStorage.removeItem(QUOTE_CACHE_KEY);
}

interface Provider {
  id: string | null;
  ukid: string | null;
  name: string | null;
  locations : string | null;
  images: Image | null;
  provider_src: string | null;
}

interface Offer {
  offer_code: string | null;
  offer_type: string | null;
  offer_scope: String | null;
  offer_amount: number;
  total_amount: number;
  offer_provider_split: Array<any>;
}

interface sellerOffer {
  offers: Array<any>;
}

interface Seller {
  id: string | null;
  base_url: string | null;
}

interface Location {
  id: string | null;
}

interface Image {
  thumbnail: string | null;
  gallery: Array<string> | null;
}

interface Category {
  code: string | null;
  name: string | null;
  parent: Category | null;
}

interface Detail {
  description: string | null;
  summary: string | null;
}

interface Stock {
  available: number;
  maximum: number;
}
interface Item {
  sku_id: string | null;
  item_id: string | null;
  name: string | null;
  images: Image | null;
  price: number;
  max_price: number;
  qty: number;
  location: Location;
  is_cancellable: boolean;
  is_returnable:boolean;
  provider: Provider;
  seller: Seller;
  category: Category;
  addons: any;
  parent_item_id: string;
  has_variant?: boolean;
  variants: any;
  customizations: any;
  attributes: any;
  is_customizable: boolean;
  detail: Detail;
  stock: Stock;
  uom?: any;
  unitized?: any;
}

interface Cart {
  transaction_id: string | null;
  transaction_id_count: number;
  items: Item[];
  summary: any[];
  response: { data: any; error: any; oosError: []; missing_ukids?: any[] };
  loader: boolean;
  offers: Offer;
  reward: Reward;
  sellerOffer: sellerOffer;
  appliedOfferRemoved: boolean;
}

interface QuoteCache {
  cartHash: string;
  transactionId: string;
  data: any;
  expiresAt?: string;
  location? : any
}

export const useCart = defineStore('cart', {
  state: (): Cart => ({
    transaction_id: null,
    transaction_id_count: 0,
    items: [],
    summary: [],
    response: { data: null, error: null, oosError: [] },
    loader: false,
    offers: {
      offer_code: null,
      offer_type: null,
      offer_scope: 'subtotal',
      total_amount:0,
      offer_amount: 0,
      offer_provider_split: [],
    },
    reward: { rewardPoints: 0, multiplier: 0, cashValue: 0 },
    sellerOffer:{
      offers: []
    },
    appliedOfferRemoved: false
  }),
  getters: {
    itemCount(state: Cart): number {
      return state.items.reduce((acc: number, item: Item) => acc + item.qty, 0);
    },
    itemTotal(state: Cart): number {
      let total = Number(state.items.reduce(
        (acc: number, item: Item) => acc + item.price * item.qty,
        0
      ));
      for (const item of state.items) {
        if (item.addons) {
          for (const k of item.addons) {
            total += Number(k.price) * Number(k.qty);
          }
        }   
      }
      return Number(parseFloat(total.toString()).toFixed(2));
    },
    provider(state: Cart) {
      const providerIds = [
        ...new Set(state.items.map((item) => item.provider.ukid)),
      ];
      return providerIds[0];
    },
    getProviders(state: Cart) {
      const providerIds = [
        ...new Set(state.items.map((item) => item.provider.ukid)),
      ];
      return providerIds;
    },
    getCartItems(state: Cart) {
      return state.items;
    },
    getCartDetails(state: Cart) {
      return state;
    },
    itemMaxTotal(state: Cart): number {
      let total = Number(state.items.reduce(
        (acc: number, item: Item) => acc + item.max_price * item.qty,
        0
      ));
      for (const item of state.items) {
        if (item.addons) {
          for (const k of item.addons) {
            total += Number(k.max_price) * Number(k.qty);
          }
        }   
      }
      return Number(parseFloat(total.toString()).toFixed(2));
    },
    isLoading(state: Cart): boolean {
      return state.loader;
    }
  },
  actions: {
    addOffer(offer: Offer) {
      this.offers.offer_code = offer.offer_code;
      this.offers.offer_type = offer.offer_type;
      this.offers.offer_scope = offer.offer_scope;
      this.offers.offer_amount = offer.offer_amount;
      this.offers.total_amount = offer.total_amount;
      this.offers.offer_provider_split = offer.offer_provider_split;
    },
    addSellerOffer(sellerOffer: sellerOffer){
      this.sellerOffer.offers = sellerOffer.offers
    },
    removeOffer(): boolean {
      const hadOffers = !!this.offers.offer_code; // true if an offer exists

      this.offers = {
        offer_code: null,
        offer_type: null,
        offer_scope:'subtotal',
        total_amount:0,
        offer_amount: 0,
        offer_provider_split: [],
      };

      return hadOffers;
    },
    addReward(data: Reward) {
      this.reward.rewardPoints = data.rewardPoints;
      this.reward.multiplier = useReward().multiplier;
      this.reward.cashValue = data.cashValue;
    },
    removeReward() {
      this.reward.rewardPoints = 0;
      this.reward.multiplier = 0;
      this.reward.cashValue = 0;
    },
    addItem(product: Item, options = { sync: false }) { 
      const existingItem = this.items.find(
        (item) => item.parent_item_id === product.parent_item_id
      );
      if (existingItem) {
        existingItem.qty++;
        if (existingItem.addons && existingItem.addons.length > 0) {
          existingItem.addons.forEach((item: any) => {
            item.qty = Number(item.qty) + 1;
          });
        }
        this.trackAddToCartEvent(
          existingItem,
          existingItem.qty,
          "Qty Increased"
        );
      } else {
        const uomObj = product.unitized?.measure;

        this.items.push({
          parent_item_id: product.parent_item_id,
          sku_id: product.sku_id,
          item_id: product.item_id,
          name: product.name,
          images: product.images,
          price: product.price,
          max_price: product.max_price,
          qty: product.qty || 1,
          location: { id: product.location.id },
          provider: {
            id: product.provider.id,
            ukid: product.provider.ukid,
            name: product.provider.name,
            locations:product.provider.locations,
            images: product.provider.images,
            provider_src: product.provider.provider_src
          },
          seller: { id: product.seller.id, base_url: product.seller.base_url },
          category: product.category,
          is_cancellable:product.is_cancellable,
          is_returnable:product.is_returnable,
          has_variant: product.has_variant || false,
          variants: product.variants,
          customizations: product.customizations,
          addons: product.addons || [],
          attributes: product.attributes || [],
          is_customizable: product.is_customizable,
          detail: product.detail,
          stock: product.stock,
          uom: product.uom?.split(' ') || (uomObj ? [uomObj.value, uomObj.unit].filter(Boolean) : null)
        });
        this.trackAddToCartEvent(product, 1, "Item Added");
      }
      if (this.removeOffer()) {
        this.appliedOfferRemoved = true;
      }
      clearQuoteStorage();
      this.resetSummary();
    },
    trackAddToCartEvent(item: Item, qty: number = item.qty, action: string) {

      const isIncrease = action === 'Item Added' || action === 'Qty Increased';
     
      const discountApplied =
        typeof item.max_price === 'number' ? item.price < item.max_price : undefined;

      const eventPayload = {
        currency: 'INR',
        value: item.price * qty,
        items: [
          {
            item_id: item.sku_id || '',
            item_name: item.name || '',
            item_category:
              item.category?.parent?.name ||
              item.category?.name ||
              undefined,
            item_brand:
              item.provider?.name ||
              undefined,
            price: item.price,
            quantity: qty,
            discount: discountApplied
              ? (item.max_price || 0) - item.price
              : undefined,
          },
        ],
      };

      // if (isIncrease) {
      //   GoogleAnalytics.addToCart(eventPayload);
      //   if (import.meta.client && typeof dht !== 'undefined') {
      //     dht('add_to_cart');
      //   }
      // } else {
      //   GoogleAnalytics.removeFromCart(eventPayload);
      // }
      
      const analyticsEvents = useAnalyticsEvents();
      useAnalytics(analyticsEvents.ADD_TO_CART, {
        product_id: item.sku_id || '',
        product_name: item.name || undefined,
        category: item.category?.parent?.name || item.category?.name || undefined,
        price: item.price,
        currency: 'INR',
        discount_applied: discountApplied,
        seller_name: item.provider?.name || item.seller?.name || undefined,
        cart_value: this.itemTotal,
        quantity: qty,
        provider_id: item.provider?.ukid || undefined,
        action_source: action,
        products: [
          {
            product_id: item.sku_id || '',
            name: item.name || '',
            category: item.category?.parent?.name || item.category?.name || undefined,
            price: item.price,
            quantity: qty,
            currency: 'INR',
          },
        ],
      });

      // Facebook Pixel AddToCart Event
      (window as any).fbq('track', 'AddToCart', {
        content_name: item.name,
        content_category: item.category?.parent?.name || item.category?.name,
        content_ids: [item.sku_id],
        content_type: 'product',
        value: item.price * qty,
        currency: 'INR'
      });
    },

    reduceQty(parentItemId: string, options = { sync: false }) {
      let existingItem = this.items.find(
        (item: Item) => (item.parent_item_id === parentItemId || item.sku_id === parentItemId)
      );
      if (existingItem) {
        if (existingItem.qty > 1) {
          existingItem.qty--;
          this.trackAddToCartEvent(
            existingItem,
            existingItem.qty,
            "Qty Decreased"
          );
        } else {
          this.remove(parentItemId);
        }
      }
      if (this.removeOffer()) {
        this.appliedOfferRemoved = true;
      } 
      clearQuoteStorage();
      this.resetSummary();
    },
    remove(parentItemId: string, options = { sync: false }) {
      
      let index = this.items.findIndex(
        (item: Item) => (item.parent_item_id === parentItemId || item.sku_id === parentItemId)
      );
      
      if (index !== -1) {
        this.trackAddToCartEvent(
            this.items[index],
            this.items[index].qty || 1,
            "Qty Decreased"
          );
        this.items.splice(index, 1);
      }
      if (this.removeOffer()) {
        this.appliedOfferRemoved = true;
      }
      clearQuoteStorage();
      this.resetSummary();
    },
    clear(offerRemovedToast: boolean = true) {
      let offerApplied = false;
      if (this.removeOffer()) {
        offerApplied = true
      }
      this.$reset();
      if(offerRemovedToast && offerApplied) {
        this.appliedOfferRemoved = true;
      }
      clearQuoteStorage();
      this.resetSummary();
    },
    resetSummary() {
      this.summary = [];
      this.response = { data: null, error: null, oosError: [] };
    },
    async fetchQuote() {
      const location = useLocation();
      const geolocation = useGeoLocation();

      // this.response.data = null;
      this.response.error = null;
      useOrder().$reset();
      if (this.items.length === 0) return;

      const currentHash = getCartHash(this.items);
      const cached = getQuoteFromStorage();
      if (cached?.expiresAt && new Date() >= new Date(cached.expiresAt)) {
        clearQuoteStorage();
      }
      const isSameGPS =
            cached?.location?.gps?.latitude === geolocation.deliveryAddress?.gps?.latitude || location.gps.latitude &&
            cached?.location?.gps?.longitude === geolocation.deliveryAddress?.gps?.longitude || location.gps.longitude ;      
      if (
        cached &&
        cached.cartHash === currentHash &&
        cached.expiresAt &&
        new Date() < new Date(cached.expiresAt)
        && isSameGPS
      ) {
        this.transaction_id = cached.transactionId;
        this.response.data = cached.data;
        this.loader = true;      
        await nextTick();
        this.loader = false; 
        return;
      }

      this.loader = true;

      try {
        const location = useLocation();
        const OfferStatus = useCheckOffer();
        const firstDeliveryOffer = OfferStatus.delivery?.[0] || {};
        const discountOffer = OfferStatus.discount || [];
        const freebieOffer = OfferStatus.freebie || []
        const slabOffer = OfferStatus.slab || []
        const comboOffer = OfferStatus.combo || []
        const payload = {
          partner: useRuntimeConfig().public.partnerCode,
          location: {
            gps: `${geolocation?.deliveryAddress?.gps?.latitude || location.gps.latitude},${geolocation?.deliveryAddress?.gps?.longitude || location.gps.longitude}`,
            area_code: `${geolocation?.deliveryAddress?.area_code || location.area_code}`,
          },
          city: { code: geolocation?.deliveryAddress?.city?.code || location.city.code },
          cart: this.items,
        };
        this.transaction_id_count = 0 ;
        const sellerGroup = groupBy(this.items, "seller.id");
        for (const sellerId in sellerGroup) {
          const providerGroup = groupBy(sellerGroup[sellerId], "provider.id");
          for (const providerId in providerGroup) {
            const categoryGroup = groupBy(providerGroup[providerId], "category.parent.code");
            for (const categoryCode in categoryGroup) {
             this.transaction_id_count++;
          }
        }
      }
        let offerIds: string[] = [];
        if (
          firstDeliveryOffer.autoApply?.toLowerCase() === "no" &&
          firstDeliveryOffer.percent === 100
        ) {
          offerIds.push(firstDeliveryOffer.id);
        }
        freebieOffer.forEach(f => {
          if (f.autoApply?.toLowerCase() === "no" && f.remainingAmount === 0 && this.items.find(k => k.sku_id === f.sku_id)) {
            offerIds.push(f.id);
          }
        });
        discountOffer.forEach(f => {
          if (f.autoApply?.toLowerCase() === "no" && f.remainingAmount === 0) {
            offerIds.push(f.id);
          }
        });
        slabOffer.forEach(({ slabs }) => {
          slabs.forEach(slab => {
            const { current_qty, minQty, maxQty } = slab;
            const withinRange =
              current_qty >= minQty 
            if (withinRange && slab.autoApply?.toLowerCase() === "no") {
              offerIds.push(slab.id);
            }
          });
        });
        comboOffer.forEach(combo => {
          const itemSkuIds = this.items.map(item => item.sku_id);
          const allMatched = combo.comboItems.every(sku => itemSkuIds.includes(sku));
        
          if (allMatched && combo.autoApply?.toLowerCase() === "no") {
            offerIds.push(combo.id);
          }
        });
        
        if (offerIds.length) {
          payload.offers = offerIds;
        }

        
        const { data: quoteData, error: quoteError } = await useAPIHandler(
          "post",
          "/quotes",
          {
            body: payload,
          }
        );

        if (quoteError) {
          throw new Error(quoteError.message);
        }
        this.transaction_id = quoteData.transaction_id;   

        const { data, error } = await useQuoteListener(
          quoteData.transaction_id,
          this.getProvidersIds
        );
        this.response.data = data;
        this.response.error = error;
        if (data) {
          saveQuoteToStorage(currentHash, quoteData.transaction_id, data);
        }
        if (data && !error) {
          GoogleAnalytics.cartApiCall({
            [EventParameter.API_RESPONSE]: 'success',
            [EventParameter.TRANSACTION_ID]: this.transaction_id,
          });

        } else if (error) {
          GoogleAnalytics.cartApiCall({
            [EventParameter.API_RESPONSE]: 'failure',
            [EventParameter.TRANSACTION_ID]: this.transaction_id,
            [EventParameter.ERROR_CODE]: error.code || '',
            [EventParameter.ERROR_MESSAGE]: error.message || '',
          });
        }
      } catch (error: any) {
        console.error("Error in fetchQuote:", error.message);
        GoogleAnalytics.cartApiCall({
          [EventParameter.API_RESPONSE]: 'failure',
          [EventParameter.TRANSACTION_ID]: this.transaction_id,
          [EventParameter.ERROR_CODE]: error?.code,
          [EventParameter.ERROR_MESSAGE]: error?.message,
        });
        this.response.error = error.message;
      } finally {
        this.loader = false;
      }
    },
    updateOOSError(data: any) {
      this.response.oosError = data;
    },

    async saveUserCart() {
      const auth = useAuth();
      if (auth?.access_token) {
        await useAPIHandler('post', '/cart', {
          body: { items: this.items },
          auth: true
        });
      }
    },
    async syncUserCart() {
      const auth = useAuth();
      if (auth?.access_token) {
        const { data } = await useAPIHandler('get', '/cart', { auth: true });
        const { items } = data;
        items.forEach((item: Item) => this.addItem(item, { sync: false }));
      }
    },
    async checkSellerOffer() {
      const sku_id: string[] = []
      const ukid: string[] = []

      for (const data of this.items) {
        sku_id.push(data.sku_id)
        if (!ukid.includes(data.provider.ukid)) {
          ukid.push(data.provider.ukid)
        }
      }

      try {
        const response = await useAPIHandler("post", "/offers/sellerOfferList", {
          body: {
            ukid,
            sku_id
          }
        })

        this.addSellerOffer({
          offers: response.data
        })
      } catch (err) {
        console.error("Error checking seller offer:", err)
      }
    }
  },
  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
